// SCR-STAFF-413 — فروش کالا، صندوق سریع (US-BIL-140؛ FLOW-05): اسکن بارکد یا جستجوی کالا (Enter ← افزودن ردیف)، مقدار، قیمت از تعرفه CAT، موجودی قفسه
// فروش INV، مشتری اختیاری (فروش گذری)، پرداخت فوری (نقد / کارتخوان / انتقال)، فروش بدون موجودی فقط با مجوز bil.product.sell_without_stock و
// ثبت با Ctrl+Enter و چاپ رسید.
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import { unwrap, type BilPaymentMethod, type BilProductSaleResponse, type BilSellableItemView, type PatientSearchItem } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { EmptyState, ErrorState, faDigits, latinDigits, LoadingState, PageHeader } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router';
import { PatientPicker } from '../patients/PatientPicker';
import { parseRials, useReceiptPrinter } from './CashierPage';
import { Amount, BIL_SESSION_PATH, bilKeys, needsReference, rial, SCR_STAFF_413 } from './common';

const METHODS: readonly BilPaymentMethod[] = ['Cash', 'CardPos', 'BankTransfer'];

interface Line {
  itemId: string | null;
  barcode: string | null;
  name: string;
  quantity: string;
}

export function ProductSalePage() {
  const { t } = useTranslation('bil');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const printReceipt = useReceiptPrinter();
  const [scan, setScan] = useState('');
  const [search, setSearch] = useState('');
  const [lines, setLines] = useState<Line[]>([]);
  const [patient, setPatient] = useState<PatientSearchItem | null>(null);
  const [method, setMethod] = useState<BilPaymentMethod>('Cash');
  const [received, setReceived] = useState('');
  const [reference, setReference] = useState('');
  const [withoutStock, setWithoutStock] = useState(false);
  const [result, setResult] = useState<BilProductSaleResponse | null>(null);
  const scanRef = useRef<HTMLInputElement | null>(null);
  const current = useQuery({ queryKey: bilKeys.session(session?.tenantId), queryFn: () => unwrap(tenantApi.GET('/api/v1/billing/cash-sessions/current')) });
  const products = useQuery({
    queryKey: bilKeys.products(session?.tenantId, search),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/billing/products', { params: { query: { q: search || undefined } } })),
  });
  const inputs = lines.map((line) => ({ itemId: line.itemId, barcode: line.barcode, quantity: Math.max(1, parseRials(line.quantity)) }));
  const preview = useQuery({
    queryKey: [...bilKeys.products(session?.tenantId, 'preview'), JSON.stringify(inputs), patient?.id ?? ''],
    queryFn: () =>
      unwrap(tenantApi.POST('/api/v1/billing/product-sales:preview', { body: { lines: inputs, patientId: patient?.id ?? null, payments: null, sellWithoutStock: null } })),
    enabled: lines.length > 0,
  });
  const payable = preview.data?.payable.amount ?? 0;
  const paid = received.trim() === '' ? payable : parseRials(received);
  const insufficient = (preview.data?.lines ?? []).some((line) => line.insufficientStock);
  const canSellWithoutStock = (preview.data?.canSellWithoutStock ?? false) && access.can('bil.product.sell_without_stock');
  const sell = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/billing/product-sales', {
          body: {
            lines: inputs,
            patientId: patient?.id ?? null,
            payments: [{ method, amount: rial(paid), reference: reference.trim() || null, cardLast4: null }],
            sellWithoutStock: insufficient && withoutStock ? true : null,
          },
          headers: { 'Idempotency-Key': key.current() },
        }),
      ),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: async (response) => {
      setResult(response);
      setLines([]);
      setReceived('');
      setReference('');
      setWithoutStock(false);
      void queryClient.invalidateQueries({ queryKey: bilKeys.all(session?.tenantId) });
      if (response.receiptId && response.receiptNumber) {
        await printReceipt(response.receiptId, response.receiptNumber);
      }
      scanRef.current?.focus();
    },
  });
  const canSubmit =
    lines.length > 0 &&
    preview.isSuccess &&
    !preview.isFetching &&
    paid >= payable &&
    (method === 'Cash' || paid === payable) &&
    !(needsReference(method) && reference.trim() === '') &&
    (!insufficient || (canSellWithoutStock && withoutStock)) &&
    !sell.isPending &&
    access.accessMode === 'Full';

  const add = (line: Line) => {
    setResult(null);
    setLines((existing) => {
      const index = existing.findIndex((item) => (line.itemId !== null && item.itemId === line.itemId) || (line.barcode !== null && item.barcode === line.barcode));
      if (index < 0) {
        return [...existing, line];
      }
      return existing.map((item, i) => (i === index ? { ...item, quantity: String(parseRials(item.quantity) + 1) } : item));
    });
  };
  const addProduct = (product: BilSellableItemView) => add({ itemId: product.itemId, barcode: null, name: product.name, quantity: '1' });

  // Enter در فیلد بارکد ← افزودن ردیف؛ Ctrl+Enter ← ثبت فروش.
  const submitRef = useRef(() => {});
  submitRef.current = () => canSubmit && sell.mutate();
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === 'Enter') {
        event.preventDefault();
        submitRef.current();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (current.isPending) {
    return <LoadingState rows={4} />;
  }
  if (current.error) {
    return <ErrorState error={current.error} onRetry={() => void current.refetch()} />;
  }
  if (!current.data?.session) {
    return (
      <Stack data-screen-id={SCR_STAFF_413} spacing={2}>
        <PageHeader titleKey="bil:products.title" />
        <EmptyState
          titleKey="bil:cashier.no_shift"
          descriptionKey="bil:cashier.no_shift_help"
          action={
            <Button component={RouterLink} to={BIL_SESSION_PATH} variant="contained">
              {t('bil:session.open')}
            </Button>
          }
        />
      </Stack>
    );
  }

  return (
    <Stack data-screen-id={SCR_STAFF_413} spacing={2}>
      <PageHeader titleKey="bil:products.title" subtitleKey="bil:products.subtitle" />
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            autoFocus
            label={t('bil:products.barcode')}
            value={scan}
            onChange={(event) => setScan(event.target.value)}
            onKeyDown={(event) => {
              const code = latinDigits(scan).trim();
              if (event.key === 'Enter' && !event.ctrlKey && code !== '') {
                event.preventDefault();
                add({ itemId: null, barcode: code, name: code, quantity: '1' });
                setScan('');
              }
            }}
            inputRef={scanRef}
            slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'bil-product-barcode' } }}
            sx={{ minWidth: 220 }}
          />
          <Autocomplete
            sx={{ flex: 1 }}
            options={products.data?.items ?? []}
            getOptionLabel={(option) => `${option.name} (${option.code})`}
            filterOptions={(options) => options}
            onInputChange={(_event, value) => setSearch(value)}
            onChange={(_event, value) => value && addProduct(value)}
            value={null}
            renderOption={(props, option) => (
              <li {...props} key={option.itemId}>
                <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ width: '100%' }}>
                  <span>{option.name}</span>
                  <Typography variant="caption" color="text.secondary">
                    {option.available !== null ? t('bil:products.available', { n: faDigits(String(option.available)) }) : null}
                  </Typography>
                </Stack>
              </li>
            )}
            renderInput={(params) => <TextField {...params} label={t('bil:products.search')} slotProps={{ htmlInput: { ...params.inputProps, 'data-testid': 'bil-product-search' } }} />}
          />
          <PatientPicker labelKey="bil:products.customer" value={patient} onChange={setPatient} testId="bil-product-patient" />
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        {lines.length === 0 ? (
          <Typography color="text.secondary">{t('bil:products.empty')}</Typography>
        ) : (
          <Table size="small" aria-label={t('bil:products.title')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('bil:products.item')}</TableCell>
                <TableCell>{t('bil:products.quantity')}</TableCell>
                <TableCell>{t('bil:products.unit_price')}</TableCell>
                <TableCell>{t('bil:cashier.payable')}</TableCell>
                <TableCell>{t('bil:products.stock')}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((line, index) => {
                const view = preview.data?.lines[index];
                return (
                  <TableRow key={`${line.itemId ?? line.barcode}-${index}`} data-testid="bil-product-line">
                    <TableCell>{view?.name ?? line.name}</TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={line.quantity}
                        onChange={(event) => setLines((existing) => existing.map((item, i) => (i === index ? { ...item, quantity: event.target.value } : item)))}
                        slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'aria-label': t('bil:products.quantity'), 'data-testid': 'bil-product-qty' } }}
                        sx={{ maxWidth: 90 }}
                      />
                    </TableCell>
                    <TableCell>{view ? <Amount money={view.unitPrice} /> : '—'}</TableCell>
                    <TableCell>{view ? <Amount money={view.payable} /> : '—'}</TableCell>
                    <TableCell data-testid="bil-product-stock" data-insufficient={view?.insufficientStock ? 'true' : 'false'}>
                      {view?.insufficientStock ? (
                        <Typography variant="body2" color="error">
                          {t('bil:products.insufficient')}
                        </Typography>
                      ) : view?.available !== null && view?.available !== undefined ? (
                        faDigits(String(view.available))
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton aria-label={t('bil:cashier.remove_row')} onClick={() => setLines((existing) => existing.filter((_item, i) => i !== index))}>
                        <CloseIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
        {preview.error ? <ErrorState error={preview.error} compact /> : null}
      </Paper>

      {lines.length > 0 ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography>{t('bil:cashier.payable')}</Typography>
              <Amount money={preview.data?.payable} testId="bil-product-payable" />
              {preview.data && preview.data.tax.amount > 0 ? (
                <Typography variant="caption" color="text.secondary">
                  {t('bil:products.tax')} <Amount money={preview.data.tax} />
                </Typography>
              ) : null}
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField select size="small" label={t('bil:products.method')} value={method} onChange={(event) => setMethod(event.target.value as BilPaymentMethod)} sx={{ minWidth: 160 }}>
                {METHODS.map((value) => (
                  <MenuItem key={value} value={value}>
                    {t(`bil:method.${value}`)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label={t('bil:products.received')}
                value={received}
                placeholder={faDigits(payable.toLocaleString('en-US'))}
                onChange={(event) => setReceived(event.target.value)}
                slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'bil-product-received' } }}
              />
              {needsReference(method) ? (
                <TextField
                  size="small"
                  required
                  label={t(method === 'CardPos' ? 'bil:cashier.pos_reference' : 'bil:cashier.transfer_reference')}
                  value={reference}
                  onChange={(event) => setReference(event.target.value)}
                  slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'bil-product-reference' } }}
                />
              ) : null}
            </Stack>
            {method === 'Cash' && paid > payable ? (
              <Typography data-testid="bil-product-change">
                {t('bil:products.change')} <Amount money={rial(paid - payable)} />
              </Typography>
            ) : null}
            {insufficient ? (
              canSellWithoutStock ? (
                <FormControlLabel
                  control={<Checkbox checked={withoutStock} onChange={(event) => setWithoutStock(event.target.checked)} data-testid="bil-product-without-stock" />}
                  label={t('bil:products.sell_without_stock')}
                />
              ) : (
                <Alert severity="warning" data-testid="bil-product-no-stock">
                  {t('bil:products.no_stock_permission')}
                </Alert>
              )
            ) : null}
            {sell.error ? <ErrorState error={sell.error} compact /> : null}
            <Button variant="contained" sx={{ alignSelf: 'flex-start' }} disabled={!canSubmit} onClick={() => sell.mutate()} data-testid="bil-product-submit">
              {t('bil:products.submit')} (Ctrl+Enter)
            </Button>
          </Stack>
        </Paper>
      ) : null}

      {result ? (
        <Alert severity="success" data-testid="bil-product-result">
          {t('bil:products.done', { number: result.receiptNumber ?? result.invoiceNumber })}
        </Alert>
      ) : null}
    </Stack>
  );
}
